import Button from "components/Button/Button"
import ModalCustom from "components/ModalCustom"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import {
  EncryptionKeys,
  encryptionController,
} from "controllers/EncryptionController"
import { encryptionHelper } from "helpers/encryption"
import { toast } from "react-toastify"
import {
  postGenerateNewRecoveryCodeMiddleware,
  postSendRecoveryCodeMiddleware,
  postUpdateSecurityCodeAndPhoneNumberMiddleware,
} from "../services/api"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import InputDefault from "components/Input/InputDefault"
import FormInputCard from "./FormInputCard"
import { useString } from "helpers/hooks"
import InputPhone from "components/Input/InputPhone"
import { useAppSelector } from "hooks/useApp"
import { maskSensitiveValue } from "helpers/utils"
import { useChangeLabelStatusInputPasswordAndConfirm } from "../auth.hook"
import { STATUS_INPUT } from "components/Input/types"
import { ReactComponent as IconWarning } from "assets/images/icons/icon-form-warning.svg"
import { getUserVaultKey } from "helpers/local_storage"

interface Props {
  onCloseModal: () => void
  handleAfterGenerate: (
    backupKeyCode: string,
    securityCode: string,
    phoneNumber: string
  ) => void
  handleOpenSetupSecurityCode: () => void
}

export const GenerateNewRecoveryCodeModal = (props: Props) => {
  const { onCloseModal, handleAfterGenerate } = props
  const userInfo = useAppSelector((state) => state.userInfo)
  const { statusInputPassword, getStatusInputPassword } =
    useChangeLabelStatusInputPasswordAndConfirm()
  const securityCode = useString("")
  const password = useString("")

  const onGenerate = async () => {
    const vaultKey = getUserVaultKey()
    if (!vaultKey) {
      toast(
        <LabelNotificationPage type="error" messenger="Vault key not found!" />
      )
      return
    }
    const { generatePasswordHashed } = encryptionController()

    const request = {
      password: await generatePasswordHashed(password.value),
    }
    postUpdateSecurityCodeAndPhoneNumberMiddleware(request)
      .then(async () => {
        const backupKeyCode = encryptionHelper.createBackupKey()
        const encryptedBackupKeyCode = await encryptionGCMHelper.encryptContext(
          backupKeyCode,
          vaultKey,
          "passphrase"
        )
        await postGenerateNewRecoveryCodeMiddleware(encryptedBackupKeyCode)
        toast(
          <LabelNotificationPage
            type="success"
            messenger={
              MESSENGER_NOTIFICATION.GENERATE_NEW_RECOVERY_CODE_SUCCESS
            }
          />
        )
        handleAfterGenerate(
          backupKeyCode,
          securityCode.value,
          userInfo.phone_number
        )
        await postSendRecoveryCodeMiddleware(
          userInfo.phone_number,
          securityCode.value,
          backupKeyCode,
          userInfo.email,
          userInfo.fullname
        )
      })
      .catch((error) => {
        const message = error.response?.data?.message ?? "Something went wrong!"
        toast(<LabelNotificationPage messenger={message} type="error" />)
      })
  }

  const handleChangeInput = (event) => {
    password.setValue(event.target.value)
    getStatusInputPassword(event.target.value)
  }

  const getRecoveryCodeMessage = (): string => {
    return `Generating a new recovery code will permanently deactivate the previous one.`
  }

  const getButtonTitle = (): string => {
    return `Generate`
  }

  const handleDisableButton = () => {
    if (
      securityCode.value.length === 4 &&
      statusInputPassword.status === STATUS_INPUT.VALID
    ) {
      return false
    }
    return true
  }

  return (
    <ModalCustom
      label="Generate new recovery code"
      widthModal={440}
      handleChangeButton={() => onCloseModal}
      styleRootBodyChildren={{
        paddingTop: 0,
      }}
      bodyChildren={
        <div className="flex flex-col">
          <div className="flex items-center">
            <p className="text-[#222222] text-[14px] font-normal leading-[24px] mb-[8px]">
              {getRecoveryCodeMessage()}
            </p>
          </div>
          <div className="flex flex-col">
            <FormInputCard title="Phone Number" customClass="relative mb-3">
              <InputPhone
                value={userInfo.phone_number}
                setValue={() => {}}
                onEnterKeyPress={() => {}}
                disabled
                inputStyle={{
                  color: "transparent",
                }}
              />
              <p className="font-normal text-[14px] leading-[24px] text-[#111111] absolute top-[35px] left-[63px]">
                {userInfo.phone_number
                  ? maskSensitiveValue(userInfo.phone_number)
                  : ""}
              </p>
            </FormInputCard>
            <FormInputCard title="Set up a passcode" required>
              <InputDefault
                value={securityCode.value}
                onChange={(el) => securityCode.setValue(el.target.value)}
                placeholder="4 characters"
                maxLength={4}
                type="password"
                isPassword
              />
              <div className="flex flex-col mt-1 gap-2">
                <p className="text-[#111111] text-[13px] leading-[22px]">
                  Your recovery code file will be sent to you by email and is
                  password-protected.
                </p>
                <div className="flex flex-col">
                  <p className="text-[#111111] text-[13px] leading-[22px] mb-1">
                    To unlock it, use a code made up of:
                  </p>
                  <p className="text-[#111111] text-[13px] leading-[22px] ml-[8px]">
                    • The last 4 digits of your phone number, followed by
                  </p>
                  <p className="text-[#111111] text-[13px] leading-[22px] ml-[8px]">
                    • Your 4-character passcode (case-sensitive; may include
                    letters, numbers, and special characters).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-[#111111] text-[13px] leading-[22px] mb-1">
                    Format:
                  </p>
                  <div className="border border-[#E4E4E4] rounded px-2 py-1 inline-block bg-[#FAFAFA]">
                    <p className="text-[#EA4545] text-[13px] leading-[22px] font-semibold">
                      [Last 4 digits of phone][4-character passcode]
                    </p>
                  </div>
                </div>
                <div className="flex flex-col">
                  <p className="font-semibold text-[#111111] text-[13px] leading-[22px] mb-1">
                    Examples:
                  </p>
                  <div className="flex items-center gap-[2px] ml-[8px]">
                    <p className="text-[#111111] text-[13px] leading-[22px]">
                      • Phone: 123456, Passcode:
                    </p>
                    <div className="border border-[#E4E4E4] rounded px-2 inline-block bg-[#FAFAFA]">
                      <p className="text-[#EA4545] text-[10px] font-semibold">
                        ABCD
                      </p>
                    </div>
                    <p className="text-[#111111] text-[13px] leading-[22px]">
                      → Unlock code:
                    </p>
                    <div className="border border-[#E4E4E4] rounded px-2 inline-block bg-[#FAFAFA]">
                      <p className="text-[#EA4545] text-[10px] font-semibold">
                        3456ABCD
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-[2px] ml-[8px] mt-[4px]">
                    <p className="text-[#111111] text-[13px] leading-[22px]">
                      • Phone: 123456, Passcode:
                    </p>
                    <div className="border border-[#E4E4E4] rounded px-2 inline-block bg-[#FAFAFA]">
                      <p className="text-[#EA4545] text-[10px] font-semibold">
                        @$cd
                      </p>
                    </div>
                    <p className="text-[#111111] text-[13px] leading-[22px]">
                      → Unlock code:
                    </p>
                    <div className="border border-[#E4E4E4] rounded px-2 inline-block bg-[#FAFAFA]">
                      <p className="text-[#EA4545] text-[10px] font-semibold">
                        3456@$cd
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <IconWarning className="w-[40px] h-[40px] text-[#E87A16] flex-shrink-0" />
                  <p className="text-[#111111] text-[13px] leading-[22px]">
                    <span className="font-semibold">Important:</span> Your
                    passcode is not stored. If it is lost, the recovery file
                    cannot be recovered.
                  </p>
                </div>
              </div>
            </FormInputCard>
            <FormInputCard required title="Current Password">
              <InputDefault
                type="password"
                isPassword
                value={password.value}
                onChange={handleChangeInput}
                onKeyPress={() => {}}
                status={statusInputPassword.status}
                labelStatus={statusInputPassword.label}
              />
            </FormInputCard>
          </div>

          <div className="flex flex-col mt-[16px] md:flex-row gap-y-3 md:gap-y-0 justify-between">
            <Button
              widthBtn={"100%"}
              title="Cancel"
              colorBtn="white"
              onClick={onCloseModal}
            />
            <Button
              widthBtn="100%"
              title={getButtonTitle()}
              colorBtn="yellow"
              onClick={onGenerate}
              disabled={handleDisableButton()}
              isDisabledBtn
            />
          </div>
        </div>
      }
    />
  )
}
